import { Ingredient } from '@cf-shared/data-model/ingredient';

/**
 * The two rules that keep a meal's food list from disappearing (#4547). They live here,
 * away from the component, so they can be tested in plain Node with no Angular and no DOM
 * -- the same shape as `cs-day-items-load-recovery.ts` and `action-buttons-owner.ts`.
 */

/** How a load of the meal's food list ended. */
export enum IngredientListLoadResult {
  /** every food was loaded and the nutrient totals were recalculated */
  Completed = 'Completed',
  /** something threw part-way; the failure was reported, and the totals are stale */
  Failed = 'Failed',
  /** the view was torn down mid-load, so the load stopped without finishing or failing */
  Destroyed = 'Destroyed',
}

/**
 * Turn an `ingredients` input that was bound to nothing into an empty list.
 *
 * Used as the `@Input({ transform })` on `IngredientList.ingredients`, so the value is
 * normalised where it LANDS rather than at one place that reads it. That matters because
 * three different things read `ingredients.length`: the load loop below, the template, and
 * the public `updateNutrientsAsync`, which other code calls on its own.
 *
 * A field initializer of `= []` does not do this job. When a parent binds `undefined`,
 * Angular writes the undefined straight over the initializer. Both parents do bind it:
 * `edit-food.html` binds `recipe?.ingredients`, which is undefined while the recipe is
 * still loading, and `edit-meal.html` binds `meal.ingredients`, which is undefined for a
 * stored meal document that has no ingredients field.
 *
 * A real list is handed back by identity, never copied: the parent goes on editing that
 * same array.
 */
export function toIngredientList(value: Ingredient[] | undefined | null): Ingredient[] {
  return value ?? [];
}

/** What `runIngredientListLoadAsync` needs from the component it is loading for. */
export interface IngredientListLoadDeps {
  /** how many ingredients there are to load */
  ingredientCount: () => number;
  /** load the display data for the ingredient at this position */
  loadOneAsync: (index: number) => Promise<void>;
  /** work out the meal's nutrient totals once every ingredient is loaded */
  updateNutrientsAsync: () => Promise<void>;
  /** whether the view has been torn down while the load was running */
  isDestroyed: () => boolean;
  /** show or hide the loading spinner, which also hides or shows the food cards */
  setSpinnerVisible: (visible: boolean) => void;
  /** redraw. Never called once `isDestroyed` is true -- that is NG0911 */
  markForCheck: () => void;
  /** report a load that failed, to telemetry AND to the user, who is still on the screen */
  reportLoadFailureToUser: (err: unknown) => void;
  /** report a load that failed to telemetry only, the user having already left the screen */
  reportLoadFailureSilently: (err: unknown) => void;
}

/**
 * Load the display data for every ingredient in a meal, and ALWAYS end with the spinner
 * cleared.
 *
 * The spinner hides the whole list of food cards, so leaving it on is not a cosmetic
 * problem: the meal renders as nothing at all, with no message, and the only way out is
 * to leave the screen. Before #4547 there was no `finally` here and every caller started
 * this as a bare `void ...`, so one throw did exactly that, permanently, while the error
 * disappeared into an unhandled promise rejection.
 *
 * The failure is caught here rather than rethrown because the calls INSIDE the component
 * do not await this, so a rethrow would just recreate the unhandled rejection for an error
 * that has already been fully reported. Callers that DO await are told what happened
 * through the returned `IngredientListLoadResult` instead.
 */
export async function runIngredientListLoadAsync(deps: IngredientListLoadDeps): Promise<IngredientListLoadResult> {
  deps.setSpinnerVisible(true);
  deps.markForCheck();
  try {
    for (let i = 0; i < deps.ingredientCount(); i++) {
      await deps.loadOneAsync(i);
      // if the user navigated away mid-load, stop -- there is nothing left to draw for
      if (deps.isDestroyed()) return IngredientListLoadResult.Destroyed;
    }
    // populate cached nutrients for display
    await deps.updateNutrientsAsync();
    return IngredientListLoadResult.Completed;
  } catch (err) {
    // if the user has already left the screen there is nobody to interrupt, so the failure
    // is reported without a message. It is still reported: the load did not recover.
    if (deps.isDestroyed()) {
      deps.reportLoadFailureSilently(err);
    } else {
      deps.reportLoadFailureToUser(err);
    }
    return IngredientListLoadResult.Failed;
  } finally {
    deps.setSpinnerVisible(false);
    // the early return above lands here too, so guard: marking a destroyed view for check
    // is NG0911
    if (!deps.isDestroyed()) {
      deps.markForCheck();
    }
  }
}
